/**
 * <cart-drawer> — coque du panier coulissant, bâtie sur <dialog> natif.
 *
 * <dialog>.showModal() fournit gratuitement le piège de focus, la fermeture par
 * Échap et le fond assombri (::backdrop). Ce composant ne gère donc que :
 *   - l'ouverture (clic sur [data-cart-open] du header, ou événement cart:open
 *     émis par cart-items.js après un ajout) ;
 *   - la fermeture (bouton [data-cart-close], clic sur le fond) ;
 *   - le retour du focus sur l'élément déclencheur à la fermeture.
 *
 * Le contenu (lignes, jauge, résumé…) est géré par <cart-items> à l'intérieur.
 *
 * Progressive enhancement : sans JavaScript, l'icône panier du header reste un
 * lien vers /cart ; ce composant n'est jamais sollicité.
 */
class CartDrawer extends HTMLElement {
  #dialog = null;
  #opener = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.querySelectorAll('[data-cart-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    // Clic sur le fond assombri : la cible est alors la <dialog> elle-même.
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.close();
    });

    // Referme avant toute navigation (retour arrière du navigateur inclus).
    window.addEventListener('pageshow', () => this.close());
  }

  open(opener) {
    if (!this.#dialog || this.#dialog.open) return;
    this.#opener = opener || document.activeElement;
    this.#dialog.showModal();
  }

  close() {
    if (!this.#dialog || !this.#dialog.open) return;
    this.#dialog.close();
    if (this.#opener instanceof HTMLElement) this.#opener.focus();
    this.#opener = null;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

// Déclencheurs globaux, liés une seule fois (le composant peut être re-rendu).
if (!window.__cartDrawerBound) {
  window.__cartDrawerBound = true;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cart-open]');
    if (!opener) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return; // pas de drawer (ex. page /cart) : on laisse le lien agir
    event.preventDefault();
    drawer.open(opener);
  });

  // Ouverture automatique après un ajout réussi (émis par cart-items.js).
  document.addEventListener('cart:open', () => {
    document.querySelector('cart-drawer')?.open();
  });
}
